import {
    Pagination,
    PaginationContainer,
    PaginationNext,
    PaginationPage,
    PaginationPageGroup,
    PaginationPrevious,
    PaginationSeparator,
} from "@ajna/pagination";
import {ChevronLeftIcon, ChevronRightIcon} from "@chakra-ui/icons";
import {Box, HStack, Icon, Select, Text} from "@chakra-ui/react";
import {clamp} from "data/utils/clamp";
import {memo} from "react";

interface PaginationWrapperProps {
  handlePageChange: (page: number) => void;
  handlePageSizeChange?: (size: number) => void;
  totalDataCount: number | undefined;
  currentPage: number;
  isDisabled: boolean;
  pagesCount: number;
  offset: number;
  pages: number[];
  size: number;
  pageSizeOptions?: readonly number[];
}

const PaginationWrapper = memo(
  ({
    handlePageChange,
    handlePageSizeChange,
    totalDataCount,
    currentPage,
    isDisabled,
    pagesCount,
    offset,
    pages,
    size,
    pageSizeOptions = [10, 25, 50, 100],
  }: PaginationWrapperProps) => {
    return (
      <>
        <Box
          bg="white"
          borderRadius="lg"
          border="1px"
          borderColor="gray.200"
          px={6}
          py={4}
        >
          <HStack
            justifyContent="space-between"
            spacing={4}
            flexDirection={["column", "row"]}
            align={["stretch", "center"]}
          >
            <HStack
              spacing={6}
              flexDirection={["column", "row"]}
              align={["flex-start", "center"]}
            >
              <Text fontSize="sm" color="gray.600" fontWeight="medium">
                Showing {offset + 1} to{" "}
                {clamp(
                  offset + Number(size),
                  Number(size),
                  totalDataCount || 0
                )}{" "}
                of {totalDataCount || 0} results
              </Text>
              {handlePageSizeChange && (
                <HStack spacing={3}>
                  <Text fontSize="sm" color="gray.700" fontWeight="medium">
                    Items per page:
                  </Text>
                  <Select
                    size="sm"
                    width="80px"
                    value={size}
                    bg="white"
                    borderColor="gray.300"
                    _focus={{
                      borderColor: "primary.500",
                      boxShadow: "0 0 0 1px var(--chakra-colors-primary-500)",
                    }}
                    onChange={(e) =>
                      handlePageSizeChange(Number(e.target.value))
                    }
                  >
                    {pageSizeOptions.map((option) => (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    ))}
                  </Select>
                </HStack>
              )}
            </HStack>
            <HStack>
              <Pagination
                pagesCount={pagesCount}
                currentPage={currentPage}
                isDisabled={isDisabled}
                onPageChange={handlePageChange}
              >
                <PaginationContainer
                  align="center"
                  justify="space-between"
                  p={4}
                  w="full"
                >
                  <PaginationPrevious
                    mr="8px"
                    boxSize="44px"
                    _focus={{ shadow: "0 0 0 3px var(--focusColor)" }}
                    _active={{ bg: "transparent" }}
                    _hover={{
                      bg: "transparent",
                    }}
                    bg="transparent"
                    border={"2px"}
                    size={"sm"}
                  >
                    <Icon as={ChevronLeftIcon} w={5} h={5} />
                  </PaginationPrevious>

                  <PaginationPageGroup
                    isInline
                    align="center"
                    separator={
                      <PaginationSeparator
                        border="1px solid #DFE3E8"
                        color="brand.grayScale"
                        bg="transparent"
                        fontSize="xs"
                        boxSize="36px"
                        jumpSize={4}
                        _active={{ bg: "transparent" }}
                        _hover={{
                          bg: "transparent",
                        }}
                        _focus={{
                          borderColor: "transparent",
                          shadow: "0 0 0 3px var(--focusColor)",
                        }}
                      />
                    }
                  >
                    {pages.map((page: any) => (
                      <PaginationPage
                        boxSize="44px"
                        bg="gray.100"
                        border="2px"
                        key={`pagination_page_${page}`}
                        page={page}
                        fontSize="xs"
                        color="brand.grayScale"
                        _active={{ bg: "primary.500", color: "white" }}
                        _hover={{
                          borderColor: "primary.500",
                          bg: "primary.500",
                          color: "white",
                        }}
                        _focus={{
                          borderColor: "primary.500",
                          background: "primary.500",
                          shadow: "0 0 0 3px var(--focusColor)",
                        }}
                        _current={{
                          borderColor: "primary.500",
                          bg: "primary.500",
                          fontSize: "xs",
                          boxSize: "44px",
                          color: "#fff",
                          _hover: {
                            borderColor: "#DFE3E8",
                            bg: "transparent",
                            color: "black",
                          },
                        }}
                      />
                    ))}
                  </PaginationPageGroup>

                  <PaginationNext
                    size={"sm"}
                    ml="8px"
                    boxSize={"44px"}
                    _active={{ bg: "transparent" }}
                    _focus={{ shadow: "0 0 0 3px var(--focusColor)" }}
                    _hover={{
                      bg: "transparent",
                    }}
                    bg="transparent"
                    border={"2px"}
                    p="0"
                  >
                    <Icon as={ChevronRightIcon} w={5} h={5} />
                  </PaginationNext>
                </PaginationContainer>
              </Pagination>
            </HStack>
          </HStack>
        </Box>
      </>
    );
  }
);

PaginationWrapper.displayName = "PaginationWrapper";

export default PaginationWrapper;
